import { ReactElement } from "react";
import clsx from "clsx";
import { Building2Icon, BriefcaseBusinessIcon, MapPinIcon } from "lucide-react";

import {
  SheetSectionContainer,
  SheetSectionEmptyText,
  SheetSectionHeader,
} from "@/components";

import { dates } from "@/lib/dates";
import { WorkExperience } from "@/types";

export const SheetWorkExperiences = ({
  containerClasses,
  showResumeWorkExperiences,
  workExperiences,
}: {
  containerClasses?: string;
  showResumeWorkExperiences?: boolean;
  workExperiences: WorkExperience[];
}) => {
  // Filter out work experiences without title and company.
  let filteredWorkExperiences = workExperiences.filter(
    (workExperience) =>
      !!workExperience.title?.trim() ||
      !!workExperience.internalCompany?.name.trim(),
  );

  // Filter out work experiences that are not on the user's profile if applicable.
  filteredWorkExperiences = showResumeWorkExperiences
    ? filteredWorkExperiences
    : filteredWorkExperiences.filter(
        (workExperience) => !workExperience.isResumeField,
      );

  const WorkExperiencesSheetSectionContainerAndHeader = ({
    children,
    isEmpty,
  }: {
    children: ReactElement;
    isEmpty?: boolean;
  }) => {
    return (
      <SheetSectionContainer className={containerClasses}>
        <SheetSectionHeader
          icon={BriefcaseBusinessIcon}
          title="Work Experience"
          isEmpty={isEmpty}
        />
        {children}
      </SheetSectionContainer>
    );
  };

  if (!filteredWorkExperiences.length) {
    return (
      <WorkExperiencesSheetSectionContainerAndHeader isEmpty>
        <SheetSectionEmptyText text="This candidate hasn't yet filled out their work experience" />
      </WorkExperiencesSheetSectionContainerAndHeader>
    );
  }

  return (
    <WorkExperiencesSheetSectionContainerAndHeader>
      <div>
        {filteredWorkExperiences.map((workExperience: WorkExperience, i) => {
          const dateDisplay =
            dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience(
              {
                startMonth: workExperience.startMonth,
                startYear: workExperience.startYear,
                endMonth: workExperience.endMonth,
                endYear: workExperience.endYear,
                current: workExperience.currentPosition,
              },
            );

          return (
            <div className="flex gap-4" key={i}>
              <div className="flex flex-col items-center">
                <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full border border-slate-400 bg-slate-100">
                  {workExperience.internalCompany?.logoUrl ? (
                    <img
                      alt={workExperience.internalCompany.name}
                      className="h-full w-full rounded-full object-cover"
                      src={workExperience.internalCompany.logoUrl}
                    />
                  ) : (
                    <Building2Icon className="h-5 w-5 text-slate-500" />
                  )}
                </div>
                {i !== filteredWorkExperiences.length - 1 &&
                  filteredWorkExperiences.length > 1 && (
                    <div className="my-1 h-4 h-full w-[1px] bg-slate-200" />
                  )}
              </div>
              <div
                className={clsx(
                  "flex w-full flex-col",
                  i !== filteredWorkExperiences.length - 1 &&
                    filteredWorkExperiences.length > 1 &&
                    "pb-4",
                )}
              >
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-700">
                    {workExperience.title}
                  </p>
                  {dateDisplay && (
                    <p className="text-sm text-slate-500">{dateDisplay}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-600">
                    {workExperience.internalCompany?.name}
                  </p>
                  {workExperience.location && (
                    <div className="flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4 text-slate-500" />
                      <p className="text-sm text-slate-500">
                        {workExperience.location}
                      </p>
                    </div>
                  )}
                </div>
                {workExperience.descriptions.filter((description) =>
                  description.trim(),
                ).length > 0 && (
                  <ul
                    // TODO: See if there is a cleaner way to do this.
                    className="list-disc pl-6"
                  >
                    {workExperience.descriptions
                      .filter((description) => description.trim())
                      .map((description, i) => {
                        return (
                          <li className="mt-2 text-sm text-slate-700" key={i}>
                            {description}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </WorkExperiencesSheetSectionContainerAndHeader>
  );
};
